import { useState } from 'react'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Save, RotateCcw, Upload, Download, TestTube, Activity } from 'lucide-react'
import { RuleCard } from '@/components/rules/RuleCard'
import { ThresholdConfigurator } from '@/components/rules/ThresholdConfigurator'
import { RuleTestingPanel } from '@/components/rules/RuleTestingPanel'
import { RuleAnalytics } from '@/components/rules/RuleAnalytics'
import { mockRules, FraudRule } from '@/services/mockRules'
import { toast } from '@/hooks/use-toast'

export function RuleManagement() {
  const [rules, setRules] = useState<FraudRule[]>(mockRules)
  const [blockingThreshold, setBlockingThreshold] = useState(70)
  const [testMode, setTestMode] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setRules((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        
        const newRules = arrayMove(items, oldIndex, newIndex)
        // Update priorities based on new order
        return newRules.map((rule, index) => ({
          ...rule,
          priority: index + 1
        }))
      })
      setHasUnsavedChanges(true)
    }
  }

  const updateRule = (updatedRule: FraudRule) => {
    setRules(rules.map(rule => 
      rule.id === updatedRule.id ? updatedRule : rule
    ))
    setHasUnsavedChanges(true)
  }

  const toggleRule = (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId)
    if (rule) {
      updateRule({ ...rule, enabled: !rule.enabled })
    }
  }

  const saveConfiguration = () => {
    // Simulate saving to backend
    setTimeout(() => {
      setHasUnsavedChanges(false)
      toast({
        title: "Configuration Saved",
        description: "Rule configuration has been updated successfully.",
      })
    }, 1000)
  }

  const resetToDefaults = () => {
    setRules(mockRules)
    setBlockingThreshold(70)
    setTestMode(false)
    setHasUnsavedChanges(false)
    toast({
      title: "Reset Complete",
      description: "All rules have been reset to default configuration.",
    })
  }

  const activeRules = rules.filter(rule => rule.enabled).length
  const totalWeight = rules.reduce((sum, rule) => 
    rule.enabled ? sum + rule.weight : sum, 0)

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Rule Management</h1>
          <p className="text-muted-foreground mt-1">
            Configure fraud detection rules and thresholds
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <Badge variant="destructive" className="animate-pulse">
              Unsaved Changes
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={resetToDefaults}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button variant="outline" size="sm">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={saveConfiguration}>
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-primary">{activeRules}</div>
            <div className="text-sm text-muted-foreground">Active Rules</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-primary">{blockingThreshold}</div>
            <div className="text-sm text-muted-foreground">Block Threshold</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-primary">{Math.round(totalWeight / activeRules) || 0}</div>
            <div className="text-sm text-muted-foreground">Avg Weight</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${testMode ? 'bg-status-pending' : 'bg-status-approved'}`} />
              <div className="text-sm font-medium">
                {testMode ? 'Test Mode' : 'Production'}
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Last modified: 2 hours ago
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="rules" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rules">Rules Configuration</TabsTrigger>
          <TabsTrigger value="threshold">Blocking Threshold</TabsTrigger>
          <TabsTrigger value="testing">
            <TestTube className="w-4 h-4 mr-2" />
            Rule Testing
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <Activity className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Fraud Detection Rules</span>
                <Badge variant="secondary">
                  {rules.length} Total Rules
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Drag rules to reorder priority. Higher rules are evaluated first.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={rules.map(rule => rule.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {rules.map((rule) => (
                    <RuleCard
                      key={rule.id}
                      rule={rule}
                      onUpdate={updateRule}
                      onToggle={toggleRule}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="threshold">
          <ThresholdConfigurator
            threshold={blockingThreshold}
            onThresholdChange={setBlockingThreshold}
            rules={rules}
          />
        </TabsContent>

        <TabsContent value="testing">
          <RuleTestingPanel
            rules={rules}
            threshold={blockingThreshold}
          />
        </TabsContent>

        <TabsContent value="analytics">
          <RuleAnalytics rules={rules} />
        </TabsContent>
      </Tabs>
    </div>
  )
}